import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@components/common/EmptyState';
import { PageHeader } from '@components/common/PageHeader';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { SectionCard } from '@components/common/SectionCard';
import {
  useApolloSequenceDetail,
  useApolloSequences,
  useCRMSettings,
  useEnrollLeadInApolloSequence,
  useLeads,
  useStopLeadInApolloSequence,
} from '@hooks/useCRM';
import { formatRelativeDate } from '@lib/format';
import type { CRMApolloSequenceOption, CRMLead } from '@/types/api';

export function CadencePage() {
  const navigate = useNavigate();
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const { data: settings } = useCRMSettings();
  const apolloReady = Boolean(settings?.apollo.enabled && settings?.apollo.has_api_key);
  const canEnrollFromCadence = Boolean(apolloReady && settings?.apollo.default_email_account_id);
  const { data: sequences, isLoading: isSequencesLoading } = useApolloSequences(undefined, apolloReady);
  const { data: leads, isLoading: isLeadsLoading } = useLeads(undefined, undefined, 200);
  const { data: selectedSequenceDetail, isLoading: isSequenceDetailLoading } = useApolloSequenceDetail(
    selectedSequenceId ?? undefined,
    apolloReady,
  );
  const enrollLeadInApolloSequenceMutation = useEnrollLeadInApolloSequence();
  const stopLeadInApolloSequenceMutation = useStopLeadInApolloSequence();

  const leadsInMotion = useMemo(
    () =>
      (leads?.items ?? [])
        .filter((lead) => Boolean(lead.apollo_sequence_id) && lead.apollo_sequence_status !== 'removed')
        .sort((left, right) => {
          const leftTime = left.apollo_last_synced_at ?? left.cadence_started_at ?? left.updated_at ?? left.created_at;
          const rightTime = right.apollo_last_synced_at ?? right.cadence_started_at ?? right.updated_at ?? right.created_at;
          return new Date(rightTime).getTime() - new Date(leftTime).getTime();
        }),
    [leads?.items],
  );

  const enrollmentLeadOptions = useMemo(
    () =>
      (leads?.items ?? [])
        .filter((lead) => lead.status !== 'converted')
        .sort((left, right) => {
          const leftName = [left.first_name, left.last_name].filter(Boolean).join(' ') || left.company_name || left.email || '';
          const rightName = [right.first_name, right.last_name].filter(Boolean).join(' ') || right.company_name || right.email || '';
          return leftName.localeCompare(rightName);
        }),
    [leads?.items],
  );

  async function handleEnrollFromCadence() {
    if (!selectedSequenceId || !selectedLeadId) {
      return;
    }
    await enrollLeadInApolloSequenceMutation.mutateAsync({
      leadId: selectedLeadId,
      payload: { sequence_id: selectedSequenceId },
    });
    setSelectedLeadId('');
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Cadence"
        subtitle="Apollo now runs outbound sequencing. Review Apollo sequences and the CRM leads currently enrolled in them."
      />

      <div className="split-layout">
        <SectionCard title="Apollo Sequences" subtitle="Live sequence options pulled from Apollo for assignment and reporting.">
          {!apolloReady ? (
            <EmptyState
              title="Apollo is not configured"
              body="Enable Apollo and provide the API key in Settings before using sequence management."
            />
          ) : isSequencesLoading ? (
            <div className="loading-row">Loading Apollo sequences…</div>
          ) : sequences?.length ? (
            <div className="stack-list">
              {sequences.map((sequence) => (
                <article
                  key={sequence.id}
                  className="stack-item stack-item-block"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedSequenceId(sequence.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedSequenceId(sequence.id);
                    }
                  }}
                >
                  <div>
                    <strong>{sequence.name}</strong>
                    <p>
                      {sequence.status ?? 'status unavailable'}
                      {sequence.owner_name ? ` • ${sequence.owner_name}` : ''}
                      {sequence.updated_at ? ` • synced ${formatRelativeDate(sequence.updated_at)}` : ''}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No Apollo sequences found"
              body="Create or publish sequences in Apollo and they will appear here for CRM assignment."
            />
          )}
        </SectionCard>

        <SectionCard title="Leads In Motion" subtitle="CRM leads currently enrolled in Apollo sequences.">
          {isLeadsLoading ? (
            <div className="loading-row">Loading cadence queue…</div>
          ) : leadsInMotion.length ? (
            <div className="stack-list">
              {leadsInMotion.map((lead) => (
                <article
                  key={lead.id}
                  className="stack-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/leads/${lead.id}`);
                    }
                  }}
                >
                  <div>
                    <strong>{[lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.email || 'Lead in motion'}</strong>
                    <p>
                      {lead.company_name || lead.job_title || 'CRM lead'}
                      {lead.apollo_sequence_id ? ` • sequence ${lead.apollo_sequence_id}` : ''}
                    </p>
                    <p>
                      {lead.apollo_sequence_status ?? 'active'}
                      {lead.apollo_last_synced_at
                        ? ` • synced ${formatRelativeDate(lead.apollo_last_synced_at)}`
                        : lead.cadence_started_at
                          ? ` • started ${formatRelativeDate(lead.cadence_started_at)}`
                          : ''}
                    </p>
                  </div>
                  <span className={`pill pill-${lead.priority}`}>{lead.priority}</span>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No Apollo enrollments yet"
              body="Once a lead is synced into Apollo and enrolled in a sequence, it will show up here."
            />
          )}
        </SectionCard>
      </div>

      {selectedSequenceId ? (
        <ApolloSequenceModal
          sequence={selectedSequenceDetail?.sequence}
          sequenceId={selectedSequenceId}
          leads={selectedSequenceDetail?.leads ?? []}
          enrolledCount={selectedSequenceDetail?.enrolled_count ?? 0}
          activeCount={selectedSequenceDetail?.active_count ?? 0}
          availableLeads={enrollmentLeadOptions}
          selectedLeadId={selectedLeadId}
          canEnroll={canEnrollFromCadence}
          isLoading={isSequenceDetailLoading}
          isEnrolling={enrollLeadInApolloSequenceMutation.isPending}
          isRemoving={stopLeadInApolloSequenceMutation.isPending}
          onSelectLead={setSelectedLeadId}
          onEnroll={() => void handleEnrollFromCadence()}
          onRemove={(leadId) => void stopLeadInApolloSequenceMutation.mutateAsync(leadId)}
          onNavigateLead={(leadId) => navigate(`/leads/${leadId}`)}
          onClose={() => {
            setSelectedSequenceId(null);
            setSelectedLeadId('');
          }}
        />
      ) : null}
    </div>
  );
}

type ApolloSequenceModalProps = {
  sequence?: CRMApolloSequenceOption;
  sequenceId: string;
  leads: CRMLead[];
  enrolledCount: number;
  activeCount: number;
  availableLeads: CRMLead[];
  selectedLeadId: string;
  canEnroll: boolean;
  isLoading: boolean;
  isEnrolling: boolean;
  isRemoving: boolean;
  onSelectLead: (leadId: string) => void;
  onEnroll: () => void;
  onRemove: (leadId: string) => void;
  onNavigateLead: (leadId: string) => void;
  onClose: () => void;
};

function ApolloSequenceModal({
  sequence,
  sequenceId,
  leads,
  enrolledCount,
  activeCount,
  availableLeads,
  selectedLeadId,
  canEnroll,
  isLoading,
  isEnrolling,
  isRemoving,
  onSelectLead,
  onEnroll,
  onRemove,
  onNavigateLead,
  onClose,
}: ApolloSequenceModalProps) {
  return (
    <div className="apollo-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="apollo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apollo-sequence-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="apollo-modal-header">
          <div className="subsection-header">
            <h3 id="apollo-sequence-modal-title">{sequence?.name ?? sequenceId}</h3>
            <p>Apollo is the execution layer for this sequence. CRM can enroll leads here, and Apollo-originated changes should be mirrored back through the inbound event relay.</p>
          </div>
          <button type="button" className="button button-secondary apollo-modal-close" onClick={onClose}>
            Close
          </button>
        </div>

        {isLoading ? (
          <div className="loading-row">Loading Apollo sequence…</div>
        ) : (
          <div className="detail-stack">
            <div className="apollo-inspector-section">
              <h4>Sequence Details</h4>
              <dl className="apollo-inspector-meta">
                <div>
                  <dt>Sequence ID</dt>
                  <dd>{sequence?.id ?? sequenceId}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{sequence?.status ?? 'Unknown'}</dd>
                </div>
                <div>
                  <dt>Owner</dt>
                  <dd>{sequence?.owner_name ?? 'Unknown'}</dd>
                </div>
                <div>
                  <dt>Updated</dt>
                  <dd>{sequence?.updated_at ? formatRelativeDate(sequence.updated_at) : 'Unknown'}</dd>
                </div>
                <div>
                  <dt>Enrolled Leads</dt>
                  <dd>{enrolledCount}</dd>
                </div>
                <div>
                  <dt>Active Leads</dt>
                  <dd>{activeCount}</dd>
                </div>
              </dl>
            </div>

            <div className="apollo-inspector-section">
              <h4>Enroll Lead From CRM</h4>
              <div className="form-grid">
                <label className="form-field form-field-wide">
                  <span>Lead</span>
                  <SearchableSelect
                    options={[
                      { id: '', label: 'Select a CRM lead' },
                      ...availableLeads.map((lead) => ({
                        id: lead.id,
                        label: [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.email || lead.company_name || 'Unnamed lead',
                        description: [
                          lead.company_name,
                          lead.apollo_sequence_id && lead.apollo_sequence_status !== 'removed'
                            ? `currently ${lead.apollo_sequence_status} in ${lead.apollo_sequence_id}`
                            : null,
                        ].filter(Boolean).join(' • '),
                      })),
                    ]}
                    value={selectedLeadId}
                    onChange={onSelectLead}
                    placeholder="Select a CRM lead"
                    clearable={false}
                  />
                </label>
              </div>
              <div className="apollo-inspector-actions">
                <button
                  type="button"
                  className="button button-primary"
                  disabled={!canEnroll || !selectedLeadId || isEnrolling}
                  onClick={onEnroll}
                >
                  {isEnrolling ? 'Enrolling…' : 'Enroll Lead in Apollo'}
                </button>
                {!canEnroll ? (
                  <p className="form-error">Set a default Apollo email account in Settings before enrolling leads from the Cadence page.</p>
                ) : null}
              </div>
            </div>

            <div className="apollo-inspector-section">
              <h4>CRM Leads In This Sequence</h4>
              {leads.length ? (
                <div className="table-wrap">
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        <th>Company</th>
                        <th>Status</th>
                        <th>Last Sync</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.id}>
                          <td>
                            <strong>{[lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.email || 'Unnamed lead'}</strong>
                            <span>{lead.job_title || lead.email || 'No title'}</span>
                          </td>
                          <td>{lead.company_name || '—'}</td>
                          <td>
                            <span className={`pill ${lead.apollo_sequence_status && lead.apollo_sequence_status !== 'removed' ? 'pill-medium' : 'pill-soft'}`}>
                              {lead.apollo_sequence_status ?? 'unknown'}
                            </span>
                          </td>
                          <td>{formatRelativeDate(lead.apollo_last_synced_at ?? lead.updated_at ?? lead.created_at)}</td>
                          <td>
                            <div className="toolbar-cluster">
                              <button type="button" className="button button-secondary" onClick={() => onNavigateLead(lead.id)}>
                                Open Lead
                              </button>
                              {lead.apollo_sequence_status !== 'removed' ? (
                                <button
                                  type="button"
                                  className="button button-secondary"
                                  disabled={isRemoving}
                                  onClick={() => onRemove(lead.id)}
                                >
                                  {isRemoving ? 'Removing…' : 'Remove'}
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState
                  title="No CRM leads tracked for this sequence"
                  body="Enroll a lead from CRM, or mirror direct Apollo enrollments back through the inbound Apollo event relay."
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
